import { usePageMeta } from "../lib/seo";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Bike, Camera, Car, Loader2, Plus, Route, Trash2, Warehouse, X, Zap } from "lucide-react";
import { Input } from "../components/ui/input";
import { Navbar } from "../components/Navbar";
import { api, formatApiError, API_BASE } from "../lib/api";

const Thumb = ({ item }) => {
  const [err, setErr] = useState(false);
  const v = item.vehicle || {};
  const Icon = v.type === "moto" ? Bike : Car;
  const src = item.has_photo
    ? `${API_BASE}/garage/${item.id}/photo?v=${item._photoV || 0}`
    : `${API_BASE}/vehicle-image?brand=${encodeURIComponent(v.brand || "")}&model=${encodeURIComponent(v.model || "")}${v.type === "moto" ? "&vtype=moto" : ""}`;
  if (err) {
    return <div className="w-full h-36 bg-secondary flex items-center justify-center"><Icon className="w-8 h-8 text-muted-foreground/50" /></div>;
  }
  return <img src={src} alt={`${v.brand} ${v.model}`} onError={() => setErr(true)} className="w-full h-36 object-cover bg-secondary" />;
};

const AddForm = ({ onAdded }) => {
  const [vtype, setVtype] = useState("moto");
  const [motos, setMotos] = useState([]);
  const [evs, setEvs] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [picked, setPicked] = useState(null);
  const [nickname, setNickname] = useState("");
  const [rider, setRider] = useState("");
  const [busy, setBusy] = useState(false);
  const [custom, setCustom] = useState(false);
  const [cm, setCm] = useState({ brand: "", model: "", tank: "", cons: "" });
  const [thanks, setThanks] = useState(null);

  useEffect(() => { api.get("/vehicles", { params: { vtype: "moto" } }).then((r) => setMotos(r.data)).catch(() => {}); }, []);
  useEffect(() => { api.get("/vehicles").then((r) => setEvs(r.data)).catch(() => {}); }, []);

  const list = vtype === "moto" ? motos : evs;
  const results = list.filter((m) => `${m.brand} ${m.model}`.toLowerCase().includes(q.toLowerCase())).slice(0, 8);
  const cmRange = (() => {
    const t = parseFloat(cm.tank), c = parseFloat(cm.cons);
    return t > 0 && c > 0 ? Math.round((t / c) * 100 / 5) * 5 : null;
  })();

  const add = async () => {
    let vehicle = picked;
    let isCustom = false;
    if (custom && vtype === "moto") {
      if (!cm.brand.trim() || !cm.model.trim()) { toast.error("Vul merk en model van je moto in"); return; }
      const tank = parseFloat(cm.tank) || 18;
      const cons = parseFloat(cm.cons) || 5;
      const slug = `${cm.brand}-${cm.model}`.toLowerCase().replace(/[^a-z0-9]+/g, "-");
      vehicle = { id: `moto-eigen-${slug}`, brand: cm.brand.trim(), model: cm.model.trim(), tank_liters: tank, consumption_l_100km: cons, range_km: Math.round((tank / cons) * 100 / 5) * 5, type: "moto" };
      isCustom = true;
    }
    if (!vehicle) { toast.error("Kies eerst een model uit de lijst"); return; }
    setBusy(true);
    try {
      await api.post("/garage", { vehicle: { ...vehicle, rider_name: rider.trim() || null }, nickname: nickname.trim() || null });
      toast.success("Toegevoegd aan je garage!");
      if (isCustom) {
        api.post("/vehicles/moto-thanks", { brand: vehicle.brand, model: vehicle.model }).then((r) => setThanks(r.data.message)).catch(() => {});
      }
      setPicked(null); setQ(""); setNickname(""); setRider(""); setCustom(false); setCm({ brand: "", model: "", tank: "", cons: "" });
      onAdded();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-card rounded-2xl p-5 space-y-4 mb-8" data-testid="garage-add-form">
      <h2 className="font-heading font-bold text-lg">Voertuig toevoegen</h2>
      <div className="flex gap-2">
        <button data-testid="garage-type-moto" type="button" onClick={() => { setVtype("moto"); setPicked(null); setQ(""); }}
          className={`px-4 py-2 rounded-full text-sm font-semibold flex items-center gap-1.5 transition-colors ${vtype === "moto" ? "bg-primary text-primary-foreground" : "bg-secondary hover:bg-secondary/70"}`}>
          <Bike className="w-4 h-4" /> Moto
        </button>
        <button data-testid="garage-type-ev" type="button" onClick={() => { setVtype("ev"); setPicked(null); setQ(""); setCustom(false); }}
          className={`px-4 py-2 rounded-full text-sm font-semibold flex items-center gap-1.5 transition-colors ${vtype === "ev" ? "bg-primary text-primary-foreground" : "bg-secondary hover:bg-secondary/70"}`}>
          <Zap className="w-4 h-4" /> EV
        </button>
      </div>
      {!custom && (
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div className="relative">
          <Input data-testid="garage-model-search" value={open ? q : picked ? `${picked.brand} ${picked.model}` : q}
            onFocus={() => { setOpen(true); setQ(""); }} onBlur={() => setTimeout(() => setOpen(false), 150)}
            onChange={(e) => { setQ(e.target.value); setOpen(true); }} placeholder={`Zoek ${vtype === "moto" ? "moto" : "EV"}...`} className="h-11 rounded-xl" />
          {open && (results.length > 0 || vtype === "moto") && (
            <div className="absolute z-30 top-full mt-1 inset-x-0 bg-card rounded-xl shadow-xl border border-border max-h-56 overflow-y-auto">
              {results.map((m) => (
                <button key={m.id} type="button" data-testid={`garage-model-option-${m.id}`}
                  className="w-full text-left px-3 py-2 text-sm hover:bg-secondary transition-colors"
                  onMouseDown={(e) => { e.preventDefault(); setPicked(m); setOpen(false); }}>
                  {m.brand} {m.model}
                </button>
              ))}
              {vtype === "moto" && (
                <button type="button" data-testid="garage-custom-moto-btn"
                  className="w-full text-left px-3 py-2.5 text-sm font-bold text-primary hover:bg-secondary transition-colors border-t border-border"
                  onMouseDown={(e) => { e.preventDefault(); setCustom(true); setPicked(null); setOpen(false); setCm({ brand: "", model: q.trim(), tank: "", cons: "" }); }}>
                  Staat je moto er niet bij? Voeg hem zelf toe
                </button>
              )}
            </div>
          )}
        </div>
        <Input data-testid="garage-nickname-input" value={nickname} onChange={(e) => setNickname(e.target.value)} placeholder='Bijnaam (bv. "De Rode")' className="h-11 rounded-xl" />
        <Input data-testid="garage-rider-input" value={rider} onChange={(e) => setRider(e.target.value)} placeholder="Naam rijder (optioneel)" className="h-11 rounded-xl" />
      </div>
      )}
      {custom && (
        <div className="bg-secondary/50 rounded-xl p-4 space-y-3" data-testid="garage-custom-moto-form">
          <p className="text-sm font-bold flex items-center gap-1.5"><Bike className="w-4 h-4 text-primary" /> Eigen moto toevoegen</p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <Input data-testid="custom-moto-brand" value={cm.brand} onChange={(e) => setCm({ ...cm, brand: e.target.value })} placeholder="Merk (bv. Honda)" className="h-11 rounded-xl" />
            <Input data-testid="custom-moto-model" value={cm.model} onChange={(e) => setCm({ ...cm, model: e.target.value })} placeholder="Model (bv. Africa Twin)" className="h-11 rounded-xl" />
            <Input data-testid="custom-moto-tank" type="number" value={cm.tank} onChange={(e) => setCm({ ...cm, tank: e.target.value })} placeholder="Tank (liter)" className="h-11 rounded-xl" />
            <Input data-testid="custom-moto-cons" type="number" step="0.1" value={cm.cons} onChange={(e) => setCm({ ...cm, cons: e.target.value })} placeholder="Verbruik l/100km" className="h-11 rounded-xl" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Input data-testid="garage-nickname-input-custom" value={nickname} onChange={(e) => setNickname(e.target.value)} placeholder='Bijnaam (bv. "De Rode")' className="h-11 rounded-xl" />
            <Input data-testid="garage-rider-input-custom" value={rider} onChange={(e) => setRider(e.target.value)} placeholder="Naam rijder (optioneel)" className="h-11 rounded-xl" />
          </div>
          <div className="flex items-center justify-between gap-3">
            <p className="text-xs text-muted-foreground font-mono" data-testid="custom-moto-range">{cmRange ? `Geschat bereik: ±${cmRange} km per tank` : "Tank + verbruik = automatisch bereik (leeg = 18 l / 5 l per 100 km)"}</p>
            <button type="button" data-testid="custom-moto-cancel" onClick={() => setCustom(false)} className="text-xs font-semibold text-muted-foreground hover:text-foreground transition-colors shrink-0">Terug naar de lijst</button>
          </div>
        </div>
      )}
      <button data-testid="garage-add-button" type="button" onClick={add} disabled={busy}
        className="flex items-center gap-2 rounded-full bg-primary text-primary-foreground hover:bg-[#1B4332] px-5 py-2.5 text-sm font-bold active:scale-95 transition-[transform,background-color] duration-200">
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Toevoegen aan garage
      </button>
      {thanks && (
        <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4" data-testid="moto-thanks-modal">
          <div className="bg-card rounded-2xl p-6 max-w-md w-full space-y-4 shadow-2xl">
            <span className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center"><Bike className="w-6 h-6 text-primary" /></span>
            <p className="text-sm leading-relaxed" data-testid="moto-thanks-message">{thanks}</p>
            <button data-testid="moto-thanks-close" type="button" onClick={() => setThanks(null)}
              className="rounded-full bg-primary text-primary-foreground hover:bg-[#1B4332] px-5 py-2.5 text-sm font-bold w-full transition-colors">Rij veilig!</button>
          </div>
        </div>
      )}
    </div>
  );
};

const GarageItem = ({ item, onChanged }) => {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const v = item.vehicle || {};
  const isMoto = v.type === "moto";
  const specs = isMoto ? `±${v.range_km} km per tank` : `${v.range_km} km · ${v.battery_kwh} kWh`;

  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("photo", file);
    try {
      await api.post(`/garage/${item.id}/photo`, fd);
      toast.success("Foto opgeslagen");
      onChanged();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const removePhoto = async () => {
    setBusy(true);
    try { await api.delete(`/garage/${item.id}/photo`); onChanged(); } catch {} finally { setBusy(false); }
  };

  const remove = async () => {
    if (!window.confirm(`${item.nickname || `${v.brand} ${v.model}`} uit je garage verwijderen?`)) return;
    try { await api.delete(`/garage/${item.id}`); toast.success("Verwijderd uit garage"); onChanged(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="bg-card rounded-2xl overflow-hidden" data-testid={`garage-item-${item.id}`}>
      <div className="relative">
        <Thumb item={item} />
        <div className="absolute top-2 right-2 flex gap-1.5">
          <button data-testid={`garage-photo-btn-${item.id}`} onClick={() => inputRef.current?.click()} disabled={busy} title="Foto uploaden"
            className="w-8 h-8 rounded-full bg-black/50 backdrop-blur text-white flex items-center justify-center hover:bg-black/70 transition-colors">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Camera className="w-4 h-4" />}
          </button>
          {item.has_photo && (
            <button onClick={removePhoto} disabled={busy} title="Foto verwijderen"
              className="w-8 h-8 rounded-full bg-black/50 backdrop-blur text-white flex items-center justify-center hover:bg-red-600/80 transition-colors">
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>
      <div className="p-4 flex items-start gap-3">
        <span className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center shrink-0 mt-0.5">
          {isMoto ? <Bike className="w-4.5 h-4.5 text-primary" /> : <Car className="w-4.5 h-4.5 text-primary" />}
        </span>
        <div className="min-w-0 flex-1">
          <p className="font-heading font-bold truncate">{item.nickname || `${v.brand} ${v.model}`}</p>
          <p className="text-xs text-muted-foreground">
            {item.nickname ? `${v.brand} ${v.model} · ` : ""}{v.rider_name ? `${v.rider_name} · ` : ""}<span className="font-mono">{specs}</span>
          </p>
          <p className="text-xs mt-1.5 flex items-center gap-1.5" data-testid={`garage-stats-${item.id}`}>
            <Route className="w-3.5 h-3.5 text-accent shrink-0" />
            {item.trips_count > 0
              ? <span className="font-semibold">{item.trips_count} {item.trips_count === 1 ? "trip" : "trips"} · <span className="font-mono">{Math.round(item.km_total).toLocaleString("nl-BE")} km</span></span>
              : <span className="text-muted-foreground">Nog geen trips gereden</span>}
          </p>
        </div>
        <button data-testid={`garage-delete-${item.id}`} onClick={remove} title="Verwijderen uit garage"
          className="shrink-0 p-2 rounded-full hover:bg-red-500/10 text-muted-foreground hover:text-red-500 transition-colors">
          <Trash2 className="w-4 h-4" />
        </button>
      </div>
      <input ref={inputRef} type="file" accept="image/*" className="hidden" data-testid={`garage-photo-input-${item.id}`}
        onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; upload(f); }} />
    </div>
  );
};

export default function GaragePage() {
  usePageMeta("Mijn Garage — VoltRoute");
  const navigate = useNavigate();
  const [items, setItems] = useState(null);

  const load = () => api.get("/garage").then((r) => setItems(r.data.map((i) => ({ ...i, _photoV: Date.now() })))).catch(() => setItems([]));
  useEffect(() => { load(); }, []);

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-10">
        <button data-testid="garage-back" onClick={() => navigate("/dashboard")} className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-4 transition-colors">
          <ArrowLeft className="w-4 h-4" /> Dashboard
        </button>
        <p className="text-xs tracking-[0.15em] uppercase font-semibold text-accent mb-2">Jouw voertuigen</p>
        <div className="flex items-center gap-3 mb-2">
          <Warehouse className="w-8 h-8 text-primary" />
          <h1 className="font-heading font-extrabold text-3xl sm:text-4xl tracking-tight">Mijn garage</h1>
        </div>
        <p className="text-sm text-muted-foreground mb-8">Bewaar jullie moto's en EV's met foto en naam — bij een nieuwe trip neem je ze in één tik over.</p>

        <AddForm onAdded={load} />

        {items === null ? (
          <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>
        ) : items.length === 0 ? (
          <p className="text-sm text-muted-foreground" data-testid="garage-empty">Nog geen voertuigen in je garage — voeg er hierboven één toe, of bewaar het voertuig van een trip via de tripdetail-pagina.</p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="garage-grid">
            {items.map((i) => <GarageItem key={i.id} item={i} onChanged={load} />)}
          </div>
        )}
      </main>
    </div>
  );
}
